import type React from 'react'

import { Navigate, useNavigate, useSearch } from '@tanstack/react-router'

import { useFirebaseUser } from '@/auth/useFirebaseUser.ts'
import { GLOBAL_SELECTOR_VALUE } from '@/components/organisms/RegionSelector'
import { AssignmentsListView } from '@/components/templates/AssignmentsListView'

import { useAssignmentsPage } from './useAssignmentsPage'

export const AssignmentsPage: React.FC = () => {
  const user = useFirebaseUser()
  const search = useSearch({ from: '/assignments/' })
  const navigate = useNavigate()
  const { assignments, regions, assignmentType } = useAssignmentsPage()

  const handleRegionChange = (regionId: string | undefined): void => {
    // Handle Global selection
    if (regionId === GLOBAL_SELECTOR_VALUE) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate({
        to: '/assignments',
        search: { assignment_type: 'global', region_id: undefined },
      })
      return
    }
    // Handle region selection (or "All")
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    navigate({
      to: '/assignments',
      search: { assignment_type: 'region', region_id: regionId },
    })
  }

  if (!user?.sys_admin) {
    return <Navigate to="/" search={{ central_id: undefined }} replace />
  }

  // Determine selected value for the selector
  // When global mode, show the global value; otherwise show region_id or undefined (for "All")

  const selectedRegionId: string | undefined =
    assignmentType === 'global' ? GLOBAL_SELECTOR_VALUE : (search as { region_id?: string }).region_id

  return (
    <AssignmentsListView
      assignments={assignments}
      regions={regions}
      selectedRegionId={selectedRegionId}
      assignmentType={assignmentType}
      onRegionChange={handleRegionChange}
    />
  )
}
